import {
  WSDivider,
  WSElement,
  WSElementColorsType,
  WSGrid,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";

type GridItemSpan = React.ComponentProps<typeof WSGrid.Item>["span"];

export type StatementColumnAlign = "start" | "end";

export type StatementColumn = {
  span: GridItemSpan;
  align?: StatementColumnAlign;
};

export type StatementRowVariant = "header" | "item" | "subtotal" | "net";

export type StatementRow = {
  id: string;
  variant: StatementRowVariant;
  /** Cell content per column, aligned by index to `columns`. */
  cells: React.ReactNode[];
};

type Props = {
  columns: StatementColumn[];
  rows: StatementRow[];
} & WSMarginProps;

const ROW_BACKGROUND: Partial<
  Record<StatementRowVariant, WSElementColorsType>
> = {
  header: "gray50",
  subtotal: "gray50",
  net: "blue50"
};

const renderCell = (
  cell: React.ReactNode,
  variant: StatementRowVariant,
  align: StatementColumnAlign
): React.ReactNode => {
  if (cell === null || cell === undefined || cell === "") {
    return null;
  }

  // Caller-styled node (e.g. a compound label) — render as-is.
  if (typeof cell !== "string" && typeof cell !== "number") {
    return cell;
  }

  const textAlign = align === "end" ? "right" : "left";

  if (variant === "header") {
    return (
      <WSText.ParagraphXs align={textAlign} color="gray500" weight="medium">
        {cell}
      </WSText.ParagraphXs>
    );
  }

  if (variant === "subtotal" || variant === "net") {
    return (
      <WSText.ParagraphSm align={textAlign} color="gray700" weight="medium">
        {cell}
      </WSText.ParagraphSm>
    );
  }

  return (
    <WSText.ParagraphSm align={textAlign} color="gray600">
      {cell}
    </WSText.ParagraphSm>
  );
};

export const StatementTable: React.FC<Props> = ({
  columns,
  rows,
  ...margin
}) => (
  <WSElement {...margin}>
    {rows.map((row, rowIndex) => (
      <React.Fragment key={row.id}>
        {rowIndex > 0 && <WSDivider />}
        <WSElement
          colorBackground={ROW_BACKGROUND[row.variant]}
          px="M"
          py={row.variant === "header" ? "S" : "M"}
        >
          <WSGrid gutter="M">
            {columns.map((column, columnIndex) => (
              <WSGrid.Item key={columnIndex} span={column.span}>
                {renderCell(
                  row.cells[columnIndex],
                  row.variant,
                  column.align ?? "start"
                )}
              </WSGrid.Item>
            ))}
          </WSGrid>
        </WSElement>
      </React.Fragment>
    ))}
  </WSElement>
);
